import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { useCurrency, CURRENCY_META } from "@/lib/currency";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Star, MapPin, ShoppingCart, MessageCircle, CreditCard, Smartphone, CalendarDays, Check, Bell } from "lucide-react";
import { toast } from "sonner";

export default function ItemDetail({ kind }) {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [comment, setComment] = useState("");
  const [rating, setRating] = useState(5);
  const [messageBody, setMessageBody] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [bookingPhone, setBookingPhone] = useState("");
  const [bookingNotes, setBookingNotes] = useState("");
  const [bookingBusy, setBookingBusy] = useState(false);
  const { add } = useCart();
  const { user } = useAuth();
  const { format, getDisplayPrice, currency } = useCurrency();
  const endpoint = kind === "service" ? "/services" : "/products";

  const load = useCallback(async () => {
    const { data: d } = await api.get(`${endpoint}/${id}`);
    setData(d);
    // If UI is in English and translation is missing, lazily fetch it
    const item = d.product || d.service;
    if (lang === "en" && item && (!item.title_en || !item.description_en)) {
      try {
        const { data: tr } = await api.post(`${endpoint}/${id}/translate?target=en`);
        setData((prev) => {
          if (!prev) return prev;
          const key = kind === "service" ? "service" : "product";
          return { ...prev, [key]: { ...prev[key], title_en: tr.title_en, description_en: tr.description_en } };
        });
      } catch (e) { console.warn("EN translation not available, falling back to FR:", e?.message); }
    }
  }, [endpoint, id, lang, kind]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-12">{t("common.loading")}</div>;
  const item = data.product || data.service;
  const displayTitle = (lang === "en" && item.title_en) ? item.title_en : item.title;
  const displayDescription = (lang === "en" && item.description_en) ? item.description_en : item.description;
  const seller = data.seller || data.provider;
  const shop = data.shop;

  const addCart = () => {
    add({
      kind, item_id: item.id, title: item.title, price: item.price,
      image: item.images?.[0] || "",
    });
    toast.success(t("item.added_to_cart"));
  };

  const buyNow = () => {
    add({
      kind, item_id: item.id, title: item.title, price: item.price,
      image: item.images?.[0] || "",
    });
    nav("/panier");
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error(t("item.review_login_required")); return nav("/connexion"); }
    try {
      await api.post("/reviews", { target_type: kind, target_id: item.id, rating, comment });
      toast.success(t("item.review_published"));
      setComment("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || t("item.err")); }
  };

  const sendMessage = async () => {
    if (!user) { toast.error(t("item.msg_login_required")); return nav("/connexion"); }
    if (!messageBody.trim()) return;
    try {
      await api.post("/messages", { to_user_id: seller.id, body: messageBody });
      toast.success(t("item.msg_sent"));
      setMessageBody("");
    } catch (err) { toast.error(t("item.err")); }
  };

  const reserveSlot = async () => {
    if (!user) { toast.error(t("item.book_login_required")); return nav("/connexion"); }
    if (!selectedSlot) { toast.error(t("item.book_pick_slot")); return; }
    setBookingBusy(true);
    try {
      await api.post("/bookings", {
        service_id: item.id,
        slot: selectedSlot,
        contact_phone: bookingPhone,
        notes: bookingNotes,
      });
      toast.success(t("item.book_success"));
      setSelectedSlot(""); setBookingNotes("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("item.err"));
    } finally { setBookingBusy(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 grid lg:grid-cols-[1fr_400px] gap-12" data-testid={`${kind}-detail-page`}>
      <div>
        <div className="grid grid-cols-1 gap-3">
          {(item.images && item.images.length ? item.images : ["https://images.unsplash.com/photo-1618574760337-2750f6251d20?w=800"]).map((img) => (
            <img key={img} src={img} alt={item.title} className="w-full aspect-[4/3] object-cover rounded-lg" />
          ))}
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <span className="label-uppercase text-xs text-[#C85A32]">{kind === "service" ? t("item.service") : t("item.product")}</span>
          <h1 className="heading font-extrabold text-3xl lg:text-4xl mt-2 text-[#111812] leading-tight" data-testid="item-title">{displayTitle}</h1>
          <div className="flex items-center gap-3 mt-3 text-sm text-[#4B5563]">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4"/> {item.location || item.zone}</span>
            <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-[#D98A2C] text-[#D98A2C]"/> {item.rating_avg > 0 ? item.rating_avg.toFixed(1) : t("item.new_no_reviews")} ({item.rating_count})</span>
          </div>
        </div>

        <div className="border-t border-b border-[#1A3322]/10 py-6">
          <p className="heading font-extrabold text-4xl text-[#1A3322]" data-testid="item-price">{format(getDisplayPrice(item))}</p>
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#4B5563]">
            {["EUR", "USD", "CDF"].filter((c) => c !== currency).map((c) => {
              const p = item.prices?.[c] != null
                ? item.prices[c]
                : (item.price || 0) * (c === "EUR" ? 1 : c === "USD" ? 1.08 : 2900);
              return <span key={c} data-testid={`price-${c}`}>≈ {format(p, c)}</span>;
            })}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-3" data-testid="payment-badges">
            <span className="inline-flex items-center gap-1.5 text-xs bg-[#F5F5F0] px-3 py-1.5 rounded-full font-medium" data-testid="pm-mm"><Smartphone className="w-3 h-3"/> {t("item.pay_mm_card")}</span>
            <div className="flex items-center gap-1.5">
              <img src="https://static-content.pawapay.io/provider_logos/vodacom.png" alt="Vodacom" className="h-5" title="Vodacom M-Pesa"/>
              <img src="https://static-content.pawapay.io/provider_logos/airtel.png" alt="Airtel" className="h-5" title="Airtel Money"/>
              <img src="https://static-content.pawapay.io/provider_logos/orange.png" alt="Orange" className="h-5" title="Orange Money"/>
            </div>
            <span className="text-[10px] text-[#4B5563]">{t("item.pay_secure")}</span>
          </div>
          {kind === "product" && <p className="text-sm text-[#4B5563] mt-3" data-testid="item-stock">{t("item.stock_line")} {item.stock > 0 ? `${item.stock} ${t("item.stock_available")}` : t("item.stock_out_text")}</p>}
          {kind === "service" && <p className="text-sm text-[#4B5563] mt-3">{t("item.service_quote")}</p>}
        </div>

        <p className="text-[#111812] leading-relaxed whitespace-pre-line" data-testid="item-description">{displayDescription}</p>

        <div className="flex gap-3">
          <Button onClick={addCart} disabled={kind === "product" && item.stock === 0}
            className="flex-1 pill-btn bg-[#1A3322] hover:bg-[#24472F] rounded-full h-12" data-testid="add-to-cart-btn">
            <ShoppingCart className="w-4 h-4 mr-2"/>{t("item.add_to_cart")}
          </Button>
          <Button onClick={buyNow} disabled={kind === "product" && item.stock === 0}
            className="flex-1 pill-btn bg-[#C85A32] hover:bg-[#D98A2C] rounded-full h-12" data-testid="buy-now-btn">
            {t("item.buy_now")}
          </Button>
        </div>

        {kind === "product" && item.stock === 0 && (
          <NotifyMeCard itemId={item.id} user={user} lang={lang} t={t}/>
        )}

        {kind === "service" && item.booking_enabled && (
          <div className="border-2 border-[#C85A32]/30 rounded-lg p-5 bg-gradient-to-br from-[#FFF7ED] to-white" data-testid="booking-widget">
            <div className="flex items-center gap-2 mb-3">
              <CalendarDays className="w-5 h-5 text-[#C85A32]"/>
              <p className="heading font-bold text-lg text-[#1A3322]">{t("item.book_title")}</p>
              {item.slot_duration_min && (
                <span className="ml-auto text-xs bg-white border border-[#C85A32]/30 text-[#C85A32] px-2 py-1 rounded-full font-semibold">
                  {item.slot_duration_min} min
                </span>
              )}
            </div>
            {(item.available_slots || []).length === 0 ? (
              <p className="text-sm text-[#4B5563]" data-testid="booking-no-slots">{t("item.book_no_slots")}</p>
            ) : (
              <>
                <p className="text-xs text-[#4B5563] mb-2">{t("item.book_pick")}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-56 overflow-y-auto pr-1" data-testid="booking-slots">
                  {(item.available_slots || []).map((s) => {
                    const d = new Date(s);
                    const dateStr = d.toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", { weekday: "short", day: "2-digit", month: "short" });
                    const timeStr = d.toLocaleTimeString(lang === "en" ? "en-GB" : "fr-FR", { hour: "2-digit", minute: "2-digit" });
                    const active = selectedSlot === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSelectedSlot(s)}
                        className={`text-left rounded-lg border-2 px-3 py-2 text-xs transition ${active ? "border-[#C85A32] bg-white shadow-sm" : "border-transparent bg-white/70 hover:border-[#C85A32]/50"}`}
                        data-testid={`booking-slot-${s}`}
                      >
                        <p className="font-semibold text-[#1A3322] capitalize">{dateStr}</p>
                        <p className="text-[#4B5563] flex items-center gap-1 mt-0.5">
                          {active && <Check className="w-3 h-3 text-[#C85A32]"/>}
                          {timeStr}
                        </p>
                      </button>
                    );
                  })}
                </div>
                {selectedSlot && (
                  <div className="mt-4 space-y-2" data-testid="booking-form">
                    <Input
                      placeholder={t("item.book_phone")}
                      value={bookingPhone}
                      onChange={(e) => setBookingPhone(e.target.value)}
                      data-testid="booking-phone"
                    />
                    <Textarea
                      placeholder={t("item.book_notes")}
                      rows={2}
                      value={bookingNotes}
                      onChange={(e) => setBookingNotes(e.target.value)}
                      data-testid="booking-notes"
                    />
                    <Button
                      onClick={reserveSlot}
                      disabled={bookingBusy}
                      className="w-full pill-btn bg-[#C85A32] hover:bg-[#D98A2C] rounded-full h-11"
                      data-testid="booking-confirm-btn"
                    >
                      <CalendarDays className="w-4 h-4 mr-2"/>{bookingBusy ? t("common.loading") : t("item.book_confirm")}
                    </Button>
                  </div>
                )}
              </>
            )}
            <p className="text-[11px] text-[#4B5563] mt-3">{t("item.book_note")}</p>
          </div>
        )}

        {seller && (
          <div className="border border-[#1A3322]/10 rounded-lg p-5 space-y-3" data-testid="seller-info">
            <p className="label-uppercase text-xs text-[#1A3322]">{t("item.seller")}</p>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#1A3322] text-white flex items-center justify-center font-bold">{seller.full_name?.[0]}</div>
              <div>
                <p className="font-semibold">{shop?.name || seller.full_name}</p>
                <p className="text-xs text-[#4B5563]">{seller.city}, {seller.country}</p>
              </div>
            </div>
            <div className="pt-2">
              <Textarea placeholder={t("item.msg_placeholder")} value={messageBody} onChange={(e) => setMessageBody(e.target.value)} data-testid="seller-message-input"/>
              <Button size="sm" onClick={sendMessage} className="mt-2 pill-btn bg-[#1A3322] rounded-full" data-testid="send-seller-message"><MessageCircle className="w-4 h-4 mr-2"/>{t("item.msg_send")}</Button>
            </div>
          </div>
        )}

        <div className="border border-[#1A3322]/10 rounded-lg p-5 space-y-4">
          <p className="label-uppercase text-xs text-[#1A3322]">{t("item.reviews")} ({data.reviews?.length || 0})</p>
          {(data.reviews || []).map((r) => (
            <div key={r.id} className="border-b last:border-b-0 border-[#1A3322]/5 pb-3 last:pb-0" data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold">{r.user_name}</span>
                <span className="flex items-center gap-0.5">{Array.from({length: r.rating}).map((_, i) => <Star key={`s-${r.id}-${i}`} className="w-3 h-3 fill-[#D98A2C] text-[#D98A2C]"/>)}</span>
              </div>
              <p className="text-sm text-[#4B5563] mt-1">{r.comment}</p>
            </div>
          ))}

          <form onSubmit={submitReview} className="pt-2 border-t border-[#1A3322]/5 space-y-3" data-testid="review-form">
            <div className="flex items-center gap-2">
              <span className="text-sm">{t("item.rate_note")}</span>
              {[1,2,3,4,5].map((n) => (
                <button key={n} type="button" onClick={() => setRating(n)} data-testid={`star-${n}`}>
                  <Star className={`w-5 h-5 ${n <= rating ? "fill-[#D98A2C] text-[#D98A2C]" : "text-[#4B5563]"}`}/>
                </button>
              ))}
            </div>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("item.review_placeholder")} data-testid="review-comment"/>
            <Button type="submit" size="sm" className="pill-btn bg-[#1A3322] rounded-full" data-testid="review-submit">{t("item.publish_review")}</Button>
          </form>
        </div>
      </div>
    </div>
  );
}


function NotifyMeCard({ itemId, user, lang, t }) {
  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.full_name || "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error(t("notifyme.err_email") || "Email requis");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${itemId}/notify-me`, { email, full_name: name });
      setDone(true);
      toast.success(data.already_subscribed
        ? (t("notifyme.already") || "Vous êtes déjà dans la liste — on vous préviendra")
        : (t("notifyme.ok") || "Parfait — on vous préviendra dès son retour en stock"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error") || "Erreur");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3" data-testid="notifyme-done">
        <Check className="w-5 h-5 text-green-600 mt-0.5"/>
        <div>
          <p className="font-semibold text-green-900">{t("notifyme.done_title") || "Alerte activée"}</p>
          <p className="text-sm text-green-800">{t("notifyme.done_desc") || "Vous serez averti par email dès que ce produit est de retour en stock."}</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-[#FFF7ED] border border-[#C85A32]/30 rounded-lg p-4 space-y-3" data-testid="notifyme-card">
      <div className="flex items-center gap-2">
        <Bell className="w-5 h-5 text-[#C85A32]"/>
        <p className="font-bold text-[#1A3322]">{t("notifyme.title") || "Prévenez-moi quand le produit revient"}</p>
      </div>
      <p className="text-xs text-[#4B5563]">
        {t("notifyme.desc") || "Laissez-nous votre email et nous vous notifierons dès que ce produit est à nouveau disponible."}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input placeholder={t("notifyme.name_ph") || "Votre prénom (facultatif)"} value={name} onChange={(e) => setName(e.target.value)} data-testid="notifyme-name"/>
        <Input type="email" placeholder={t("notifyme.email_ph") || "email@example.com"} value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="notifyme-email"/>
      </div>
      <Button type="submit" disabled={busy} className="pill-btn w-full bg-[#C85A32] hover:bg-[#D98A2C] rounded-full h-10 text-sm font-bold" data-testid="notifyme-submit">
        {busy ? (t("notifyme.sending") || "Envoi…") : (
          <><Bell className="w-4 h-4 mr-2"/>{t("notifyme.cta") || "M'avertir dès son retour"}</>
        )}
      </Button>
    </form>
  );
}
